import {
  ACTION,
  Button,
  FormControlSizes,
  HedgeCommandEnum,
  HedgeControlStatusEnum,
  IconName,
  NotificationVariants,
  Tooltip,
  Z_INDEX,
  useGlobalToasts,
} from '@talos/kyoko';
import { useCallback, useState } from 'react';
import { firstValueFrom } from 'rxjs';
import { useHedgeRuleCommandMutation } from '../../containers/Trading/Markets/PositionAutoHedgingRules/queries';
import { useFeatureFlag, useRoleAuth } from '../../hooks';
import { useHedgePositionStatus } from '../../providers/HedgePositionStatusProvider';
import { normalizeHedgeCrossAssetRiskGroup } from './crossAssetRiskGroup';

export const SynchronizeHedgerButton = () => {
  const { isAuthorized } = useRoleAuth();
  const { add: addToast } = useGlobalToasts();
  const { mutateAsync, isPending } = useHedgeRuleCommandMutation();
  const sendSynchronizeCommand = useCallback(() => {
    mutateAsync({ Command: HedgeCommandEnum.Synchronize })
      .then(() => {
        addToast({
          text: 'Sent synchronize command',
          variant: NotificationVariants.Positive,
        });
      })
      .catch(() => {
        addToast({
          text: 'Failed to send Synchronize command',
          variant: NotificationVariants.Negative,
        });
      });
  }, [addToast, mutateAsync]);

  return (
    isAuthorized(ACTION.HEDGER_SYNCHRONIZATION) && (
      <Button
        loading={isPending}
        onClick={sendSynchronizeCommand}
        size={FormControlSizes.Tiny}
        startIcon={IconName.Refresh}
      >
        Synchronize
      </Button>
    )
  );
};

const RESTART_ALL_TOOLTIP =
  'All errored hedge rules will be restarted. If position thresholds are breached, hedger will start hedging';

export const RestartAllHedgerButton = () => {
  const { isAuthorized } = useRoleAuth();
  const { add: addToast } = useGlobalToasts();
  const { mutateAsync } = useHedgeRuleCommandMutation();
  const { hedgePositionStatusObs } = useHedgePositionStatus();
  const { enableHedgeRuleCrossAssetRiskGroup } = useFeatureFlag();
  const [isPending, setIsPending] = useState(false);

  const restartAllErroredRules = useCallback(async () => {
    setIsPending(true);
    try {
      const hedgePositionStatuses = await firstValueFrom(hedgePositionStatusObs);
      const erroredRules = Array.from(hedgePositionStatuses.values()).filter(
        status => status.HedgeControlStatus === HedgeControlStatusEnum.Error
      );

      if (erroredRules.length === 0) {
        addToast({
          text: 'No errored rules to restart',
          variant: NotificationVariants.Positive,
        });
        return;
      }

      const CONCURRENCY_LIMIT = 5;
      const restartResults: PromiseSettledResult<unknown>[] = [];
      for (let i = 0; i < erroredRules.length; i += CONCURRENCY_LIMIT) {
        const batchResults = await Promise.allSettled(
          erroredRules.slice(i, i + CONCURRENCY_LIMIT).map(status =>
            mutateAsync({
              Command: HedgeCommandEnum.Activate,
              HedgeRuleID: status.HedgeRuleID,
              HedgeControlType: 'Position',
              ...(enableHedgeRuleCrossAssetRiskGroup
                ? { CrossAssetRiskGroup: normalizeHedgeCrossAssetRiskGroup(status.CrossAssetRiskGroup) }
                : {}),
            })
          )
        );
        restartResults.push(...batchResults);
      }

      const failedCount = restartResults.filter(r => r.status === 'rejected').length;
      if (failedCount > 0) {
        addToast({
          text: `Failed to restart ${failedCount} of ${erroredRules.length} errored rule(s)`,
          variant: NotificationVariants.Negative,
        });
      } else {
        addToast({
          text: `Restarted ${erroredRules.length} errored rule(s)`,
          variant: NotificationVariants.Positive,
        });
      }
    } catch {
      addToast({
        text: 'Failed to restart errored rules',
        variant: NotificationVariants.Negative,
      });
    } finally {
      setIsPending(false);
    }
  }, [addToast, enableHedgeRuleCrossAssetRiskGroup, hedgePositionStatusObs, mutateAsync]);

  return (
    isAuthorized(ACTION.HEDGER_SYNCHRONIZATION) && (
      <Tooltip tooltip={RESTART_ALL_TOOLTIP} zIndex={Z_INDEX.dropdown}>
        <Button
          loading={isPending}
          onClick={restartAllErroredRules}
          size={FormControlSizes.Tiny}
          startIcon={IconName.Refresh}
        >
          Restart All
        </Button>
      </Tooltip>
    )
  );
};
